import { ModalWithIcon } from '@components/ModalWithIcon/ModalWithIcon'
import { Container } from '../shared.styles'
import LoadingModal from '@components/LoadingModal/LoadingModal'
import { Balance } from '@hooks/useBalance'
import { redirectToTelegramSupport } from '@lib/utils'
import { useState } from 'react'
import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { MigrationStatus, useMigrationStatus } from '../shared'
import Snackbar from '@components/Snackbar/Snackbar'
import { migrate } from '../migrationApi'

type TemporaryTokenMigrationProps = {
  balance?: Balance,
  isBalanceLoading?: boolean,
  balanceError?: any
}

const getAction = (status?: MigrationStatus, t?: any, handleMigrate?: () => void, handleContactSupport?: () => void) => {
  switch (status) {
    case "JETTON_RETURN_FAILED":
      return {
        message: t?.('common.tokenMigration.dialog.retryMessage') || 'Previous migration attempt was unsuccessful. Please retry.',
        button: t?.('common.tokenMigration.dialog.retryButton') || 'Retry migration',
        onPress: handleMigrate || (() => migrate())
      }
    case "SC_MINT_FAILED":
      return {
        message: t?.('common.tokenMigration.dialog.supportMessage') || 'There was an issue with the migration please reach out to our customer service.',
        button: t?.('common.tokenMigration.dialog.contactButton') || 'Contact us',
        onPress: handleContactSupport || (() => redirectToTelegramSupport('JagadCS_bot'))
      }
    case "JETTON_RETURN_INITIATED":
    case "JETTON_RETURN_SUCCESS":
    case "SC_MINT_INITIATED":
    case "COMPLETED_SC_MINT_SUCCESS":
    case "COMPLETED_NO_JS":
      return {
        message: "",
        button: "",
        onPress: () => { }
      }
    case "PROCESSING":
    case "INITIATED":
    case "MIGRATION_INITIATED":
    case undefined:
    default:
      return {
        message: t?.('common.tokenMigration.dialog.upgradeMessage') || 'Dear user, we are upgrading JIDR. Please click the button below to begin the process.',
        button: t?.('common.tokenMigration.dialog.migrateButton') || 'Migrate',
        onPress: handleMigrate || (() => migrate())
      }
  }
}

function TemporaryTokenMigrationDialog({
  balance,
  isBalanceLoading,
  balanceError
}: TemporaryTokenMigrationProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [isMigrating, setIsMigrating] = useState(false)
  const [hasContactedSupport, setHasContactedSupport] = useState(false)
  const [isError, setIsError] = useState(false)

  // Always call hooks first, before any early returns
  const { data: migration, isLoading: isMigrationStatusLoading, isError: isMigrationStatusError } = useMigrationStatus()

  // Now check for early return conditions
  if (balanceError || balance === undefined || balance?.confirmed_balance === 'unavailable' || balance?.legacy_balance === 'unavailable') {
    return <></>
  }

  if (isMigrationStatusError) {
    return <></>
  }

  const handleMigrate = async () => {
    setIsMigrating(true)
    setIsError(false)
    try {
      await migrate()
      navigate('/token-migration-lock')
    } catch (error) {
      setIsMigrating(false)
      setIsError(true)
    }
  }

  const handleContactSupport = () => {
    redirectToTelegramSupport('JagadCS_bot')
    // Don't dismiss dialog for SC_MINT_FAILED
    if (status !== 'SC_MINT_FAILED') {
      setHasContactedSupport(true)
    }
  }

  const status = migration?.status
  const action = getAction(status, t, handleMigrate, handleContactSupport)

  // show modal if user has legacy balance AND needs to take action, OR if migration failed
  const shouldShowModal = (balance.legacy_balance > 0 || status === 'SC_MINT_FAILED' || status === 'JETTON_RETURN_FAILED') && action.message !== ""

  // If no modal should be shown and not in migration process, return empty
  if (!shouldShowModal && !isMigrating && !hasContactedSupport) {
    return <></>
  }

  // Hide component after contacting support (except for SC_MINT_FAILED)
  if (hasContactedSupport && status !== 'SC_MINT_FAILED') {
    return <></>
  }

  return (
    <Container>
      <LoadingModal
        visible={isBalanceLoading || isMigrationStatusLoading || isMigrating}
      />
      <ModalWithIcon
        title={''}
        description={action.message}
        onPositive={action.onPress}
        positiveText={action.button}
        type="void"
        visible={shouldShowModal}
      />
      <Snackbar
        width='90%'
        isVisible={isError}
        duration={8000}
        message={t('errors.genericServerDescription')}
      />
    </Container>
  )
}

export default TemporaryTokenMigrationDialog